import { type ServiceDiscountTypes, type UserView } from '@clients/AuthApi';

/**
 * Sets cookies params and it's expiration date
 *
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
export const setAuthCookie = (name: string, value: string, days: number) => {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

/**
 * Erases the set cookie
 *
 * @param {string} name
 */
export const eraseCookie = (name: string) => {
    document.cookie = `${name}=; Max-Age=-99999999;`;
};

/**
 * Gets the cookie from browser
 *
 * @param {string} name
 * @returns {string | undefined}
 */
export const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        const last = parts.pop();
        return last?.split(';').shift();
    }
};

export interface Utm {
    utmMedium?: string;
    utmCampaign?: string;
    utmSource?: string;
    utmCreative?: string;
    utmContent?: string;
    utmKey?: string;
    utmTerm?: string;
    utmServiceType?: string;
    url?: string;
    referrer: string;
    utmId?: string;
}

export enum LoginError {
    NewPassword = 'NewPassword',
    NewPasswordConfirm = 'NewPasswordConfirm',
    MobileOrEmail = 'MobileOrEmail',
    CurrentPassword = 'CurrentPassword',
    Token = 'Token',
    Mobile = 'Mobile',
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'Email',
    Password = 'Password',
    Gender = 'Gender',
}

/**
 * Indicates the type of user
 */
export enum UserType {
    User,
    Organization,
    Intermediate,
}

/**
 * The status of organizational user terms being accepted
 *
 * @enum {number}
 */
export enum TermsStatus {
    Accepted = 'accepted',
    NotAccepted = 'notAccepted',
}

export type UserInfo = Partial<UserView>;

export enum AuthStep {
    /**
     * The initial step, that gets either email or mobile for login or signup
     */
    EnterMobileOrEmail = 'EnterMobileOrEmail',

    /**
     * When the user exists and want to login via password
     */
    LoginByPassword = 'LoginByPassword',

    /**
     * When the user exists and want to login via token
     */
    LoginByToken = 'LoginByToken',

    /**
     * The signup form
     */
    SignupInfo = 'SignupInfo',

    /**
     * When the user wants to change the password, to verify his mobile
     */
    ChangePasswordToken = 'ChangePasswordToken',

    /**
     * When the user that wants to change password has enter his mobile, to get the new password
     */
    ChangePasswordMobile = 'ChangePasswordMobile',

    /**
     * When the user that wants to change password has verified his mobile, to get the new password
     */
    ChangePassword = 'ChangePassword',

    /**
     * When the authorization process was successfull and if was organizational
     */
    SwitchAccount = 'SwitchAccount',

    /**
     * When the authorization process was somehow successfull
     */
    Success = 'Success',
}

export type UserCredential = {
    token: string;
    title: string;
    type: UserType;
    uuid: string;
    expireDateMilSec: number | null;
};

export enum AuthLocalStorageKeys {
    localUUID = 'localUuid',
    termsStatus = 'termsStatus',
    selectedCredentialIndex = 'selectedCredentialIndex',
    credentials = 'credentials',
    lastCheckUserHasPurchase = 'last-check-user-has-purchase',
    takhfifan = 'takhfifan',
    utm = 'utm',
    lastCheckout = 'lastCheckout',
    customDiscountsInfo = 'customDiscountsInfo',
    utmCache = 'utmCache',
}

export enum AuthSessionStorageKeys {
    rayanCard = 'rayanCard',
    tara = 'tara',
    sessionId = 'sessionId',
}

export type OpenedBySources =
    | 'reserveCard'
    | 'trackingBtn'
    | 'incOfferRegister'
    | 'firstPurchase'
    | null;

export type CustomDiscount = Record<ServiceDiscountTypes, number>;
export type CustomDiscountRegisterData = {
    data: CustomDiscount;
    date: number;
    userId: number;
};
